import { Add as AddIcon, PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import { Grid, Typography } from "@mui/material";
import barcodeScanner from "components/barcodeScan/barcodeScanner";
import FullScreenDialog from "components/FullScreenDialog";
import { StyledContainer } from "components/StyledContainer";
import { StyledFab } from "components/StyledFab";
import { intlFormatPrice } from "helpers";
import technicianSettings from "helpers/technicianSettings";
import { AddPartEnum } from "models/AddPartEnum";
import { TabProps } from "models/TabProps";
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import {
  getFrequencyParts,
  getRequestableParts,
  getSuggestedParts,
} from "store/slices/cache.store";
import { selectSelectedJob } from "store/slices/jobs.store";
import { AddPart } from "./AddPart";
import { UsedPartComponent } from "./UsedPart";

interface PartTabProps extends TabProps {
  parts: any[];
  dialogType: AddPartEnum;
  hasPreOrderedParts?: boolean;
  setOpenPreOrderedPartsDialog?: (open: boolean) => void;
  hideAddButton?: boolean;
  setTabIndex: Dispatch<SetStateAction<string>>;
}

export const PartTab: FC<PartTabProps> = (props) => {
  const {
    parts,
    dialogType,
    hideAddButton,
    hasPreOrderedParts,
    setOpenPreOrderedPartsDialog,
    setTabIndex,
    setFab,
  } = props;
  const dispatch = useAppDispatch();
  const isOnline = useAppSelector(selectConnectionStatus);
  const job = useAppSelector(selectSelectedJob);
  const { loading: cacheLoading } = useAppSelector((state) => state.cache);
  const intl = useIntl();
  const [openAddPartDialog, setOpenAddPartDialog] = useState(false);
  const machineId = job?.machine?.id ?? "";
  const modelGuid = job?.machine?.modelGuid ?? null;
  const allowSalesPrice = technicianSettings.syncCostPrice || technicianSettings.syncCostPrice;
  const enableFreqList = technicianSettings.syncFreqList && modelGuid;

  useEffect(() => {
    if (!isOnline) return;
    dispatch(getRequestableParts({ force: false }));
  }, [dispatch, isOnline]);

  useEffect(() => {
    if (!isOnline) return;
    dispatch(getSuggestedParts({ machineId }));
    if (enableFreqList) dispatch(getFrequencyParts({ modelGuid }));
  }, [dispatch, machineId, isOnline, enableFreqList, modelGuid]);

  useEffect(() => {
    if (hasPreOrderedParts && setOpenPreOrderedPartsDialog) {
      setOpenPreOrderedPartsDialog(true);
    }
  }, [hasPreOrderedParts, setOpenPreOrderedPartsDialog]);

  const [countEditItems, setCount] = useState<number>(0);

  const setCountEditItems = useCallback(
    (amount: number) => {
      setCount(countEditItems + amount);
    },
    [countEditItems],
  );

  useEffect(() => {
    const fab = !hideAddButton ? (
      <StyledFab
        disabled={countEditItems > 0}
        color="primary"
        aria-label={intl.formatMessage({ id: "general.add" })}
        onClick={() => {
          if (hasPreOrderedParts && setOpenPreOrderedPartsDialog) {
            setOpenPreOrderedPartsDialog(true);
            setOpenAddPartDialog(true);
          }

          setOpenAddPartDialog(true);
        }}
        data-testid="PartTabAddButton"
      >
        <AddIcon />
      </StyledFab>
    ) : undefined;
    if (setFab) {
      setFab(fab);
    }
  }, [
    countEditItems,
    hasPreOrderedParts,
    hideAddButton,
    intl,
    setFab,
    setOpenPreOrderedPartsDialog,
  ]);

  const partListItems = parts.map((part, index) => {
    return (
      part.part && (
        <UsedPartComponent
          index={index}
          maxQuantity={part.maxQuantity}
          part={part.part}
          usedPart={part}
          key={`${index}-${part.part.articleNo}-${part.part.stockId}-${part.part.stockStore}`}
          setCountEditItems={setCountEditItems}
          dialogType={dialogType}
          setTabIndex={setTabIndex}
          disabled={part.uploaded || part.pendingUpload}
        />
      )
    );
  });
  const totalSalesPrice = parts.reduce(
    (sum, part) => (part.part ? sum + (part.part.salesPrice ?? 0) * part.part.quantity : sum),
    0,
  );

  const noPartsLabel =
    dialogType === AddPartEnum.AddPart ? (
      <FormattedMessage id="part.noPartsUsed" />
    ) : (
      <FormattedMessage id="part.noPartsRequested" />
    );

  useEffect(() => {
    barcodeScanner.initialize();
    return () => {
      barcodeScanner.turnOff();
    };
  }, []);

  return (
    <StyledContainer>
      {!partListItems ||
        (partListItems?.length === 0 && (
          <Grid container direction="column" alignItems="center">
            <Grid item>
              <PlaylistAddIcon color="secondary" fontSize="large" />
            </Grid>
            <Grid item>
              <Typography color="secondary">{noPartsLabel}</Typography>
            </Grid>
          </Grid>
        ))}
      {allowSalesPrice && partListItems && partListItems?.length > 0 && (
        <Grid container mb={2}>
          <strong>
            <FormattedMessage id="visit.totalPrice" />
          </strong>
          : {intlFormatPrice(intl, totalSalesPrice)}
        </Grid>
      )}
      <Grid container spacing={1}>
        {partListItems && partListItems?.length > 0 ? partListItems : null}
      </Grid>
      <FullScreenDialog
        child={
          <AddPart
            setOpenAddPartDialog={setOpenAddPartDialog}
            loading={cacheLoading.availableParts || cacheLoading.requestableParts}
            dialogType={dialogType}
          />
        }
        title={`${job?.externalId} / ${intl.formatMessage({ id: "job.parts" })}`}
        centerTitle
        goingBack
        open={openAddPartDialog}
        setOpen={(open: boolean) => setOpenAddPartDialog(open)}
      />
    </StyledContainer>
  );
};
